/**
 * Generator 6: Page content — (authenticated)/<n>/<n>-page-content.tsx
 * Generates client component with search, filters, bulk delete, sort order dialog, and table
 */

import { flattenFields } from '@core-engine/schema/field-helpers/index.js'
import type { GeneratedFile, GeneratorOptions, Schema } from '@core-engine/schema/types.js'
import { createGeneratedFile } from '@core-engine/utils/generated-files.js'
import {
  pluralize,
  singularize,
  singularizeLabel,
  toCamelCase,
  toKebabCase,
  toPascalCase
} from '@core-engine/utils/string.js'
import { resolveSchemaActionImportPath } from '@next-adapter/config/paths.js'
import { buildEntityDevModeIntegration } from '@next-adapter/generators/dev-mode-integration.js'
import { resolveEntityFilters } from '@next-adapter/generators/entity-filters.js'

function formatSelectOptions(options: { label: string; value: string }[]): string {
  return `[${options
    .map(
      (option) =>
        `{ "label": ${JSON.stringify(option.label)}, "value": ${JSON.stringify(option.value)} }`
    )
    .join(', ')}]`
}

function formatPropertyAccess(objectName: string, key: string): string {
  return /^[A-Za-z_$][\w$]*$/.test(key)
    ? `${objectName}.${key}`
    : `${objectName}[${JSON.stringify(key)}]`
}

// ============================================================================
// Public API
// ============================================================================

export interface PageContentGeneratorResult {
  files: GeneratedFile[]
}

/**
 * Generate page content component for entity list view
 */
export function generatePageContent(
  schema: Schema,
  pagesDir: string,
  options: GeneratorOptions = {}
): PageContentGeneratorResult {
  const actionImportPath = resolveSchemaActionImportPath(schema.name, options.outputNamespace)
  const singular = singularize(schema.name)
  const plural = pluralize(schema.name)
  const Singular = toPascalCase(singular)
  const Plural = toPascalCase(plural)
  const camelPlural = toCamelCase(plural)
  const fileName = `${toKebabCase(plural)}-page-content.tsx`
  const adminRoutePath = options.adminRoutePath ?? '/admin'
  const devModeIntegration = buildEntityDevModeIntegration(
    schema,
    options.adminDir ?? 'admin',
    actionImportPath,
    options.adminImportAlias
  )
  const hasCreate = schema.actions?.create ?? false
  const hasDelete = schema.actions?.delete ?? false
  const resolvedFilters = resolveEntityFilters(schema).map((filter) => ({
    ...filter,
    variableName: toCamelCase(filter.key),
    pascalName: toPascalCase(filter.key),
    fromVariableName: `${toCamelCase(filter.key)}From`,
    toVariableName: `${toCamelCase(filter.key)}To`
  }))
  const hasFilters = resolvedFilters.length > 0
  const distinctFilters = resolvedFilters.filter((filter) => filter.source === 'distinct')

  // --- Build title field fallback for sort order dialog labels ---
  const flatFields = flattenFields(schema.fields || [])
  const fieldNames = new Set(flatFields.map((f) => f.name))
  const labelParts: string[] = []
  if (fieldNames.has('title')) labelParts.push('item.title as string')
  if (fieldNames.has('name')) labelParts.push('item.name as string')
  labelParts.push('String(item.id)')
  const labelExpr =
    labelParts.length === 1
      ? labelParts[0]
      : `(${labelParts.slice(0, -1).join(' || ')} || ${labelParts[labelParts.length - 1]})`

  const lucideIcons: string[] = ['GripVertical']

  // --- Hook imports (only for distinct filters) ---
  const hookImportNames: string[] = []
  if (distinctFilters.length > 0) {
    for (const filter of distinctFilters) {
      hookImportNames.push(`use${Plural}Distinct${filter.pascalName}`)
    }
  }

  let imports = `'use client'

import type { Setting } from '@admin/actions/settings'
import { useIsFetching, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { ${lucideIcons.join(', ')} } from 'lucide-react'
${hasCreate ? "import Link from 'next/link'\n" : ''}import { parseAsInteger, parseAsString${hasFilters ? ', useQueryStates' : ''}${hasDelete ? ', parseAsArrayOf' : ''}, useQueryState } from 'nuqs'
import * as React from 'react'
import { toast } from 'sonner'
${hasFilters ? "import { EntityFiltersBar } from '@admin/components/shared/entity-filters-bar'\n" : ''}import { SortOrderDialog } from '@admin/components/shared/sort-order-dialog'
import { PageHeader } from '@admin/components/shared/page-header'
import { Button } from '@admin/components/ui/button'
import { ${devModeIntegration.buttonExportName} } from '${devModeIntegration.buttonImportPath}'
`

  if (hasDelete) {
    imports += `import { DeleteDialog } from '@admin/components/shared/delete-dialog'
`
  }

  if (hookImportNames.length > 0) {
    imports += `import {
  ${hookImportNames.join(',\n  ')}
} from '@admin/hooks/use-${schema.name}'
`
  }

  imports += `import type { ${Singular} } from '@admin/actions/${actionImportPath}'
import { get${Plural}, bulkUpdate${Plural}SortOrder } from '@admin/actions/${actionImportPath}'
${hasDelete ? `import { deleteBulk${Plural} } from '@admin/actions/${actionImportPath}'\n` : ''}import { ${Plural}Table } from './${toKebabCase(plural)}-table'
`

  const filterStateLogic = hasFilters
    ? `  const filterParsers = {
${resolvedFilters
  .flatMap((filter) =>
    filter.type === 'date-range'
      ? [
          `    '${filter.key}From': parseAsString.withDefault('')`,
          `    '${filter.key}To': parseAsString.withDefault('')`
        ]
      : [`    '${filter.key}': parseAsString.withDefault('')`]
  )
  .join(',\n')}
  } as const
  const [filterValues, setFilters] = useQueryStates(filterParsers)
${resolvedFilters
  .flatMap((filter) =>
    filter.type === 'date-range'
      ? [
          `  const ${filter.fromVariableName} = ${formatPropertyAccess('filterValues', `${filter.key}From`)}`,
          `  const ${filter.toVariableName} = ${formatPropertyAccess('filterValues', `${filter.key}To`)}`
        ]
      : [`  const ${filter.variableName} = ${formatPropertyAccess('filterValues', filter.key)}`]
  )
  .join('\n')}
${resolvedFilters
  .map((filter) =>
    filter.source === 'distinct'
      ? `  const { data: ${filter.variableName}Options } = use${Plural}Distinct${filter.pascalName}()`
      : ''
  )
  .filter(Boolean)
  .join('\n')}
`
    : ''

  const filterOptionsLogic = resolvedFilters
    .map((filter) => {
      if (filter.type === 'date-range') {
        return ''
      }

      if (filter.source === 'distinct') {
        return `  const ${filter.variableName}SelectOptions = (${filter.variableName}Options ?? []).map((option) => ({
    label: option,
    value: option
  }))`
      }

      return `  const ${filter.variableName}SelectOptions = ${formatSelectOptions(filter.options)}`
    })
    .filter(Boolean)
    .join('\n')

  const clearFilterObject = resolvedFilters
    .flatMap((filter) =>
      filter.type === 'date-range'
        ? [`        '${filter.key}From': null`, `        '${filter.key}To': null`]
        : [`        '${filter.key}': null`]
    )
    .join(',\n')

  const activeFilterCountExpr = `[search${resolvedFilters
    .map((filter) =>
      filter.type === 'date-range'
        ? `, (${filter.fromVariableName} || ${filter.toVariableName})`
        : `, ${filter.variableName}`
    )
    .join('')}].filter(Boolean).length`

  const filterConfigEntries = hasFilters
    ? resolvedFilters
        .map((filter) => {
          if (filter.type === 'date-range') {
            return `    { key: '${filter.key}', label: '${filter.label}', type: 'date-range' as const, from: ${filter.fromVariableName}, to: ${filter.toVariableName} }`
          }
          return `    { key: '${filter.key}', label: '${filter.label}', type: 'select' as const, value: ${filter.variableName}, options: ${filter.variableName}SelectOptions }`
        })
        .join(',\n')
    : ''

  const deleteLogic = hasDelete
    ? `
  const [selectedIds, setSelectedIds] = useQueryState(
    'selected',
    parseAsArrayOf(parseAsString).withDefault([])
  )
  const [isPending, startTransition] = React.useTransition()

  const handleBulkDelete = () => {
    startTransition(async () => {
      try {
        const result = await deleteBulk${Plural}(selectedIds)

        if (result.success) {
          toast.success(
            \`\${selectedIds.length} ${singular}\${selectedIds.length > 1 ? 's' : ''} deleted successfully\`
          )
          queryClient.refetchQueries({ queryKey: ['${plural}'] })
          void setSelectedIds([])
        } else {
          toast.error(result.error || 'Failed to delete ${plural}')
        }
      } catch (error) {
        toast.error('An error occurred')
        console.error(error)
      }
    })
  }
`
    : ''

  const sortOrderLogic = `
  const [sortDialogOpen, setSortDialogOpen] = React.useState(false)
  const { data: sortOrderSummary } = useQuery({
    queryKey: ['${plural}', 'sort-order-summary'],
    queryFn: () => get${Plural}({ limit: 1, offset: 0 })
  })
  const sortOrderTotalRows = sortOrderSummary?.total ?? 0

  const fetchSortPage = React.useCallback(
    async (offset: number, limit: number) => {
      const result = await get${Plural}({ limit, offset })
      return {
        items: (result.${camelPlural} ?? []).map((item) => ({
          id: item.id,
          label: ${labelExpr}
        })),
        total: result.total
      }
    },
    []
  )

  const handleSaveSortOrder = React.useCallback(
    async (updates: { id: string; sortOrder: number }[]) => {
      const result = await bulkUpdate${Plural}SortOrder(updates)
      if (result.success) {
        queryClient.refetchQueries({ queryKey: ['${plural}'] })
      }
      return result
    },
    [queryClient]
  )
`

  const deleteButton = hasDelete
    ? `
            {selectedIds.length > 0 && (
              <DeleteDialog
                count={selectedIds.length}
                entityName="${singular}"
                onConfirm={handleBulkDelete}
                isPending={isPending}
              />
            )}`
    : ''

  const createButton = hasCreate
    ? `<Button asChild>
              <Link href="${adminRoutePath}/${schema.name}/new">
                Create ${singularizeLabel(schema.label)}
              </Link>
            </Button>`
    : ''

  const rearrangeButton = `
            <Button variant="outline" onClick={() => setSortDialogOpen(true)} disabled={sortOrderTotalRows === 0}>
              <GripVertical className="size-4" />
              Rearrange
            </Button>`

  const filtersActionsContent = hasDelete
    ? `          <div className="flex items-center gap-1">${deleteButton}${rearrangeButton}
          </div>`
    : `          <div className="flex items-center gap-1">${rearrangeButton}
          </div>`

  const filterPropsStr = hasFilters
    ? resolvedFilters
        .flatMap((filter) =>
          filter.type === 'date-range'
            ? [
                `${filter.fromVariableName}={${filter.fromVariableName}}`,
                `${filter.toVariableName}={${filter.toVariableName}}`
              ]
            : [`${filter.variableName}={${filter.variableName}}`]
        )
        .join(' ')
    : ''
  const allTableProps = filterPropsStr ? `search={search} ${filterPropsStr}` : 'search={search}'
  const tableProps = hasDelete
    ? `columns={columns} selectedIds={selectedIds} setSelectedIds={setSelectedIds} ${allTableProps}`
    : `columns={columns} selectedIds={[]} setSelectedIds={() => {}} ${allTableProps}`

  const sortOrderDialog = `
        <SortOrderDialog
          open={sortDialogOpen}
          onOpenChange={setSortDialogOpen}
          queryKey={['${plural}']}
          fetchPage={fetchSortPage}
          onSave={handleSaveSortOrder}
          title="Rearrange ${schema.label}"
          description="Drag items to reorder them."
        />`

  const content = `${imports}
interface ${Plural}PageContentProps<TValue> {
  columns: ColumnDef<${Singular}, TValue>[]
  initialSettings?: Setting | null
}

export function ${Plural}PageContent<TValue>({
  columns,
  initialSettings
}: ${Plural}PageContentProps<TValue>) {
  const queryClient = useQueryClient()
  const isSearchFetching = useIsFetching({ queryKey: ['${plural}'] }) > 0
  const [search, setSearch] = useQueryState('q', parseAsString.withDefault(''))
  const [, setPageIndex] = useQueryState('page', parseAsInteger.withDefault(0))
${filterStateLogic ? `\n${filterStateLogic}` : ''}${deleteLogic}${sortOrderLogic}
${filterOptionsLogic ? `${filterOptionsLogic}\n` : ''}  const activeFilterCount = ${activeFilterCountExpr}

  const handleSearch = React.useCallback((value: string | null) => {
    React.startTransition(() => {
      void setPageIndex(0)
      void setSearch(value)
    })
  }, [setSearch, setPageIndex])

  const handleClearAll = React.useCallback(() => {
    React.startTransition(() => {
      void setPageIndex(0)
      void setSearch(null)
${
  hasFilters
    ? `      void setFilters({
${clearFilterObject}
      })`
    : ''
}
    })
  }, [setSearch, setPageIndex${hasFilters ? ', setFilters' : ''}])
${
  filterConfigEntries
    ? `
  const filters = [
${filterConfigEntries}
  ]
`
    : ''
}
  return (
    <React.Fragment>
      <PageHeader
        title="${schema.label}"
        actions={
          <div className="flex items-center gap-1">
            <${devModeIntegration.buttonExportName} initialSettings={initialSettings} />
${createButton ? `            ${createButton}` : ''}
          </div>
        }
      />
${
  hasFilters
    ? `
      <EntityFiltersBar
        filters={filters}
        activeFilterCount={activeFilterCount}
        onFilterChange={(updates) => {
          React.startTransition(() => {
            void setPageIndex(0)
            void setFilters(updates)
          })
        }}
        onClearAll={handleClearAll}
        search={search}
        searchPlaceholder="Search ${schema.label.toLowerCase()}..."
        isPending={isSearchFetching}
        onSearch={handleSearch}
        actions={
${filtersActionsContent}
        }
      />`
    : `
      <div className="flex items-center justify-end">
${filtersActionsContent}
      </div>`
}
      <main className="space-y-4 px-4 pb-4 flex-1">
        <${Plural}Table ${tableProps} />${sortOrderDialog}
      </main>
    </React.Fragment>
  )
}
`

  return {
    files: [
      createGeneratedFile(`${pagesDir}/${schema.name}/${fileName}`, content),
      ...devModeIntegration.files
    ]
  }
}
